import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, Download, RefreshCw, Ban, Nfc, Printer, ScanLine } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../common";
import ServiceCV from "../ServiceCV";
import ScanLinkDialog from "../ScanLinkDialog";

export default function EmployeeDetailsTab({ cv, employeeId, onOpenFeedback, onReload }) {
  const navigate = useNavigate();
  const emp = cv?.employee;
  const [qr, setQr] = useState(null);
  const [nfcState, setNfcState] = useState("");
  const [scanOpen, setScanOpen] = useState(false);

  const loadQr = useCallback(() => api.get(`/employees/${employeeId}/qr?fmt=png`).then((r) => setQr(r.data)).catch(() => {}), [employeeId]);
  useEffect(() => { loadQr(); }, [loadQr]);

  const reissue = async () => { await api.post(`/employees/${employeeId}/identity/reissue`); toast.success("New QR identity issued"); loadQr(); };
  const revoke = async () => { await api.post(`/employees/${employeeId}/identity/revoke`); toast.success("Identity revoked"); loadQr(); };
  const downloadQr = () => {
    if (!qr?.png) return;
    const a = document.createElement("a");
    a.href = qr.png; a.download = `${emp.name}-servantage-qr.png`; a.click();
  };

  const provisionNfc = async () => {
    if (!("NDEFReader" in window)) {
      setNfcState("unsupported");
      toast.error("NFC programming is not supported on this device/browser. Use QR instead.");
      return;
    }
    try {
      setNfcState("writing");
      let url = qr?.url;
      if (!url) {
        const r = await api.get(`/employees/${employeeId}/qr?fmt=png`);
        setQr(r.data);
        url = r.data.url;
      }
      const nfcUrl = url + (url.includes("?") ? "&" : "?") + "s=nfc";
      // eslint-disable-next-line no-undef
      const writer = new NDEFReader();
      await writer.write({ records: [{ recordType: "url", data: nfcUrl }] });
      await api.post(`/employees/${employeeId}/nfc-programmed`);
      setNfcState("done");
      toast.success("NFC tag programmed");
      onReload?.();
    } catch (e) {
      setNfcState("error");
      toast.error("NFC write failed or cancelled.");
    }
  };

  return (
    <div className="space-y-6">
      <ServiceCV cv={cv} onOpenFeedback={onOpenFeedback} />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <Label>Dynamic QR Identity</Label>
          <p className="mt-1 text-sm text-slate-500">Resolves to a secure public token — never a raw employee ID.</p>
          <Button variant="outline" size="sm" onClick={() => setScanOpen(true)} data-testid="scan-link-open" className="mt-3">
            <ScanLine className="mr-1 h-4 w-4" /> Link a pre-printed lanyard
          </Button>
          <div className="mt-4 flex flex-col items-center">
            {!qr && <Button onClick={loadQr} data-testid="generate-qr"><QrCode className="mr-1 h-4 w-4" /> Generate QR</Button>}
            {qr && (
              <>
                <img src={qr.png} alt="QR" className="h-56 w-56 rounded-lg border border-slate-200 p-2" data-testid="qr-image" />
                <code className="mt-3 rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">{qr.url}</code>
                {!qr.token_active && <span className="mt-2 text-xs font-bold text-red-600">Identity revoked — not accepting feedback</span>}
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <Button variant="outline" onClick={downloadQr} data-testid="download-qr"><Download className="mr-1 h-4 w-4" /> Download PNG</Button>
                  <Button variant="outline" onClick={() => navigate(`/app/employees/${employeeId}/print`)} data-testid="print-card"><Printer className="mr-1 h-4 w-4" /> Print card</Button>
                  <Button variant="outline" onClick={reissue} data-testid="reissue-qr"><RefreshCw className="mr-1 h-4 w-4" /> Reissue</Button>
                  <Button variant="outline" onClick={revoke} data-testid="revoke-qr" className="text-red-600"><Ban className="mr-1 h-4 w-4" /> Revoke</Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <Label>NFC Provisioning</Label>
          <p className="mt-1 text-sm text-slate-500">Tap an NFC sticker to write the employee's feedback link. QR always works as fallback.</p>
          <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
            Status: {emp?.nfc_provisioned ? <span className="font-bold text-emerald-600">Provisioned</span> : <span className="text-slate-400">Not provisioned</span>}
          </div>
          <Button onClick={provisionNfc} disabled={nfcState === "writing"} className="mt-4 bg-[#4338CA] hover:bg-[#3730A3]" data-testid="provision-nfc">
            <Nfc className="mr-1 h-4 w-4" /> {nfcState === "writing" ? "Tap a tag…" : emp?.nfc_provisioned ? "Re-program NFC tag" : "Program NFC tag"}
          </Button>
          {nfcState === "writing" && (
            <p className="mt-3 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-800" data-testid="nfc-writing">
              Hold an NFC sticker to the back of your phone to write the feedback link…
            </p>
          )}
          {nfcState === "done" && (
            <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="nfc-done">
              Tag programmed. Scanning it opens this employee's feedback page.
            </p>
          )}
          {nfcState === "error" && (
            <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-800" data-testid="nfc-error">
              NFC write failed or was cancelled. Try again, or use the QR code.
            </p>
          )}
          {nfcState === "unsupported" && (
            <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="nfc-unsupported">
              NFC programming is only available on Chrome for Android. Use the QR code on this device instead.
            </p>
          )}
        </div>
      </div>

      <ScanLinkDialog open={scanOpen} onOpenChange={setScanOpen} employeeId={employeeId}
        onLinked={() => { loadQr(); onReload?.(); }} />
    </div>
  );
}
